import { all, create, MathNode } from "mathjs";
import { Map } from "immutable";
import { formatMessage } from "lib/globalize-util";
import { set } from "lodash";
import { ALPHA_REGEX } from "./field_validation";
import { isValidNumber } from "lib/number";
import { RESERVED_KEY, ScopeKeys } from "helpers/product-collection";
import { amphibiousGetIn } from "helpers/objUtils";
import { Item, SIZE_PACKS_DELIMITER } from "constants/product-collection";
import { mapByIndexGeneric } from "helpers/sizing";
type ValidationFunction = Function & { rawArgs?: boolean };
type ValidationFunctionHolder = { [key: string]: ValidationFunction };
const impureFunctions: ValidationFunctionHolder = {
    // Determine if matrix or number is divisible by second arg
    isDivisible(args, _math, scope) {
        const [originalDividend, originalDivisor]: MathNode[] = args;
        let dividendArr, divisor;

        if (originalDividend.isSymbolNode || originalDividend.isFunctionNode || originalDividend.isArrayNode) {
            const result = originalDividend.compile().evaluate(scope);
            dividendArr = Array.isArray(result) ? result : result.isMatrix ? result.toArray() : [result];
        } else {
            dividendArr = [originalDividend.value];
        }

        if (originalDivisor.isSymbolNode || originalDivisor.isFunctionNode) {
            divisor = originalDivisor.compile().evaluate(scope);
        } else {
            // It is a constant node.
            divisor = originalDivisor.value;
        }
        return dividendArr.every((value) => value % divisor === 0);
    },
    /*
        Return the result of evaluating predicate (second arg) on elements with the same index over matrices.
        This function represents each element iterated as u1, u2, ..., ui, ..., un, where i is the i-th matrix
        and n is the total number of matrices passed to the function before the predicate.

        p1, p2, ... is the source variable

        For example, let m1 = [1, 2, 3], m2 = [4, 5, 6], m3 = [1, 2, 'a'].
        predicate(m1,m2,m3, isValidNumber(u1) and isValidNumber(u2) and isValidNumber(u3)) will return false, because it will yield the result of:
        isValidNumber(u1) and isValidNumber(u2) and isValidNumber(u3) and (with u1=1, u2=4, u3=1)
        isValidNumber(u1) and isValidNumber(u2) and isValidNumber(u3) and (with u1=2, u2=5, u3=2)
        isValidNumber(u1) and isValidNumber(u2) and isValidNumber(u3) and (with u1=3, u2=6, u3='a')
     */
    predicate(args, _math, scope) {
        const pred: MathNode = args[args.length - 1];
        const compiledPredicate = pred.compile();
        const matrices = args.slice(0, args.length - 1).map((matrix: MathNode) => {
            const items = matrix.compile().evaluate(scope);
            if (items.toArray) {
                return items.toArray();
            } else {
                return items;
            }
        });
        const maxLength = matrices[0].length;
        let result = true;
        for (let i = 0; i < maxLength; i++) {
            result =
                result &&
                compiledPredicate.evaluate({
                    ...scope,
                    ...matrices.reduce((acc, matrix, j) => {
                        return { ...acc, [`u${j + 1}`]: matrix[i] ?? "", [`p${j + 1}`]: matrix };
                    }, {}),
                });
        }
        return result;
    },
    // Get size pack objects from context.
    sizePacks(_, __, scope) {
        const context = scope[RESERVED_KEY + ScopeKeys.Context];
        if (!context?.sizePacks) {
            return [];
        }
        const rowData = scope[RESERVED_KEY + ScopeKeys.Data];
        const selectedPacks = amphibiousGetIn(rowData, Item.FIELDS.PACKS_SELECTED, "").split(SIZE_PACKS_DELIMITER);
        const brand = amphibiousGetIn(rowData, Item.FIELDS.BRAND_ID, "");
        const packsForBrand = context.sizePacks[brand];
        if (!packsForBrand) {
            return [];
        }
        return selectedPacks.map((pack) => packsForBrand[pack] ?? {});
    },
};
const pureFunctions: ValidationFunctionHolder = {
    // Split string by delimiter
    split(text, delimiter) {
        if (!text) {
            return [];
        }
        return text.split(delimiter);
    },
    // Check if argument is a valid number
    isValidNumber,
    // Do nothing; this function only exists to bypass messaging logic
    passThru(_, predicate) {
        return predicate;
    },
    // Check if argument is null, undefined, the empty string or a non-boolean non-numeric falsy object
    isEmpty(arg) {
        if (!["number", "boolean"].includes(typeof arg)) {
            return !arg;
        }
        return false;
    },
    // Check if argument is alphanumeric
    isAlpha(arg) {
        const type = typeof arg;
        if (type === "number") {
            return true;
        } else if (type === "string") {
            return ALPHA_REGEX.test(arg);
        }
        return false;
    },
    contains(value, arg) {
        return value.toLowerCase().includes(arg.toLowerCase());
    },
    allowedSymbols(value, arg) {
        const newRegExp = new RegExp(`^[A-Za-z0-9${setSymbolsToRegExp(arg._data)}]*$`);
        return newRegExp.test(value);
    },
    mapByIndex(value: string, lookupArray, mappingArray) {
        if (!lookupArray || !mappingArray) {
            return "";
        }
        const newLookupArray = lookupArray.toArray ? lookupArray.toArray() : lookupArray;
        const newMappingArray = mappingArray.toArray ? mappingArray.toArray() : mappingArray;
        return mapByIndexGeneric(value, newLookupArray, newMappingArray) ?? "";
    },
};
const setSymbolsToRegExp = (symbols: string[]): string =>
    symbols.reduce((acc, curr) => `${acc}${["-", "."].includes(curr) ? `\\${curr}` : curr === " " ? "\\s" : curr}`, "");

const serializeFormulaNode = (
    node: MathNode,
    fieldNameToDisplayMapping: Map<string, string>,
    negative = false
): string => {
    let message;
    if (node.isFunctionNode || node.isOperatorNode) {
        const fn = node.isFunctionNode ? node.name : node.fn;
        if (node.op !== "not") {
            message = validationMessagesFixtures[fn]
                ? validationMessagesFixtures[fn](node.args, fieldNameToDisplayMapping, negative)
                : serializeFormulaNode(node.args[0], fieldNameToDisplayMapping, negative);
        } else {
            message = serializeFormulaNode(node.args[0], fieldNameToDisplayMapping, true);
        }
    } else if (node.isParenthesisNode) {
        message = `(${serializeFormulaNode(node.content, fieldNameToDisplayMapping)})`;
    } else if (node.isAccessorNode) {
        const { index, object } = node as any;
        message = fieldNameToDisplayMapping.get(`${object.name}.${index.dimensions[0].value}`);

        if (message) {
            return message;
        }

        message = `${serializeFormulaNode(object, fieldNameToDisplayMapping)} ${serializeFormulaNode(
            index.dimensions[0],
            fieldNameToDisplayMapping
        )}`;
    } else if (node.isConstantNode) {
        message = `${node.value}`;
    } else if (node.isArrayNode) {
        const parts = [];
        node.forEach((childNode) => parts.push(serializeFormulaNode(childNode, fieldNameToDisplayMapping)));
        message = parts.join(", ");
    } else if (node.isSymbolNode) {
        message = fieldNameToDisplayMapping.getIn([node.name, "display_name"]) || node.name;
    }
    return message;
};

export const fixPredicate = (args) => {
    const newArgs = [...args];
    let pred = newArgs.pop();
    const matrices = newArgs;
    pred.traverse((node, path, parent) => {
        if (node.isSymbolNode && /^u\d+$/.test(node.name)) {
            const index = +node.name.replace("u", "") - 1;
            if (parent) {
                set(parent, path, matrices[index]);
            } else {
                pred = matrices[index];
            }
        }
    });
    return pred;
};

const validationMessagesFixtures = {
    isDivisible(args: MathNode[], fieldNameToDisplayMapping: Map<string, string>) {
        return formatMessage("[|dividend|] should be divisible by [|divisor|]", {
            dividend: serializeFormulaNode(args[0], fieldNameToDisplayMapping),
            divisor: serializeFormulaNode(args[1], fieldNameToDisplayMapping),
        });
    },
    larger(args: MathNode[], fieldNameToDisplayMapping: Map<string, string>) {
        return formatMessage("|leftOperand| should be bigger than |rightOperand|", {
            leftOperand: serializeFormulaNode(args[0], fieldNameToDisplayMapping),
            rightOperand: serializeFormulaNode(args[1], fieldNameToDisplayMapping),
        });
    },
    smaller(args: MathNode[], fieldNameToDisplayMapping: Map<string, string>) {
        return formatMessage("|leftOperand| should be less than |rightOperand|", {
            leftOperand: serializeFormulaNode(args[0], fieldNameToDisplayMapping),
            rightOperand: serializeFormulaNode(args[1], fieldNameToDisplayMapping),
        });
    },
    max(args: MathNode[], fieldNameToDisplayMapping: Map<string, string>) {
        return formatMessage("Maximum of |element|", {
            element: serializeFormulaNode(args[0], fieldNameToDisplayMapping),
        });
    },
    bulkUnits() {
        return formatMessage("Bulk units");
    },
    allocationUnits() {
        return formatMessage("Allocation units");
    },
    predicate(args: MathNode[], fieldNameToDisplayMapping: Map<string, string>) {
        const pred = fixPredicate(args);
        return serializeFormulaNode(pred, fieldNameToDisplayMapping);
    },
    isEmpty(args: MathNode[], fieldNameToDisplayMapping: Map<string, string>, negative) {
        return formatMessage(`|operand| should ${negative ? "not" : ""} be empty`, {
            operand: serializeFormulaNode(args[0], fieldNameToDisplayMapping),
        });
    },
    isAlpha(args: MathNode[], fieldNameToDisplayMapping: Map<string, string>) {
        return formatMessage("|operand| should be alphanumeric", {
            operand: serializeFormulaNode(args[0], fieldNameToDisplayMapping),
        });
    },
    contains(args: MathNode[], fieldNameToDisplayMapping: Map<string, string>, negative: boolean) {
        return formatMessage(`|leftOperand| should ${negative ? "not" : ""} contain |rightOperand|`, {
            leftOperand: serializeFormulaNode(args[0], fieldNameToDisplayMapping),
            rightOperand: serializeFormulaNode(args[1], fieldNameToDisplayMapping),
        });
    },
    allowedSymbols(args: MathNode[], fieldNameToDisplayMapping: Map<string, string>, negative: boolean) {
        const symbols = serializeFormulaNode(args[1], fieldNameToDisplayMapping).split(",");
        const values = symbols.reduce((acc, current) => acc + ` [${current}]`, "");
        return formatMessage(
            `|leftOperand| should ${negative ? "not" : ""} contain valid characters only |rightOperand|`,
            {
                leftOperand: serializeFormulaNode(args[0], fieldNameToDisplayMapping),
                rightOperand: values,
            }
        );
    },
    or(args: MathNode[], fieldNameToDisplayMapping: Map<string, string>) {
        return formatMessage("|leftOperand| OR |rightOperand|", {
            leftOperand: serializeFormulaNode(args[0], fieldNameToDisplayMapping),
            rightOperand: serializeFormulaNode(args[1], fieldNameToDisplayMapping),
        });
    },
    and(args: MathNode[], fieldNameToDisplayMapping: Map<string, string>) {
        return formatMessage("|leftOperand| AND |rightOperand|", {
            leftOperand: serializeFormulaNode(args[0], fieldNameToDisplayMapping),
            rightOperand: serializeFormulaNode(args[1], fieldNameToDisplayMapping),
        });
    },
    size(args: MathNode[], fieldNameToDisplayMapping: Map<string, string>) {
        return formatMessage("Length of |operand|", {
            operand: serializeFormulaNode(args[0], fieldNameToDisplayMapping),
        });
    },
    isValidNumber(args: MathNode[], fieldNameToDisplayMapping: Map<string, string>) {
        return formatMessage("|operand| should be a valid number", {
            operand: serializeFormulaNode(args[0], fieldNameToDisplayMapping),
        });
    },
    sizePacks() {
        return formatMessage("Size Packs");
    },
    equalText(args: MathNode[], fieldNameToDisplayMapping: Map<string, string>, negative: boolean) {
        return formatMessage(`|leftOperand| should ${negative ? "not" : ""} be equal to |rightOperand|`, {
            leftOperand: serializeFormulaNode(args[0], fieldNameToDisplayMapping),
            rightOperand: serializeFormulaNode(args[1], fieldNameToDisplayMapping),
        });
    },
};
Object.values(impureFunctions).forEach((fn) => (fn.rawArgs = true));

export const mathJSMessagingInstance = create(all, {});
export const buildValidationMessageForFormula = (
    originalFormula: string,
    fieldNameToDisplayMapping: Map<string, string>
) => {
    const node = mathJSMessagingInstance.parse(originalFormula);
    return serializeFormulaNode(node, fieldNameToDisplayMapping);
};

const validationLogic = { ...impureFunctions, ...pureFunctions };
export { serializeFormulaNode, validationLogic };
